import BigNumber from 'bignumber.js';
import type { Address } from 'viem';

import {
  legacyPoolComptrollerAbi,
  primeAbi,
  resilientOracleAbi,
  vaiControllerAbi,
} from 'libs/contracts';
import type { Asset, TokenBalance } from 'types';
import type { GetPoolsInput, GetPoolsOutput, PrimeApy, VTokenBalance } from '../types';
import { appendPrimeSimulationDistributions } from './appendPrimeSimulationDistributions';
import { formatOutput } from './formatOutput';
import { type ApiTokenMetadata, getApiPools } from './getApiPools';
import { getUserCollateralAddresses } from './getUserCollateralAddresses';
import { getUserPrimeApys } from './getUserPrimeApys';
import { getUserTokenBalances } from './getUserTokenBalances';
import { getUserVaiBorrowBalance } from './getUserVaiBorrowBalance';

export interface GetPoolsQueryOutput extends GetPoolsOutput {
  tokenMetadataMapping: Record<string, ApiTokenMetadata>;
}

export const getPools = async ({
  publicClient,
  chainId,
  accountAddress,
  primeContractAddress,
  poolLensContractAddress,
  legacyPoolComptrollerContractAddress,
  vaiControllerContractAddress,
  resilientOracleContractAddress,
  venusLensContractAddress,
  tokens,
  isEModeFeatureEnabled,
}: GetPoolsInput): Promise<GetPoolsQueryOutput> => {
  const vai = tokens.find(token => token.symbol === 'VAI');

  const [
    apiPoolsResult,
    currentBlockNumberResult,
    unsafePrimeVTokenAddressesResult,
    primeMinimumXvsToStakeMantissaResult,
    userPrimeTokenResult,
    vaiPriceMantissaResult,
    vaiRepayRateMantissaResult,
  ] = await Promise.all([
    getApiPools({ chainId }),
    // Fetch current block number
    publicClient.getBlockNumber(),
    // Prime related calls
    primeContractAddress
      ? publicClient.readContract({
          abi: primeAbi,
          address: primeContractAddress,
          functionName: 'getAllMarkets',
        }) // TODO: get from API
      : undefined,
    primeContractAddress
      ? publicClient.readContract({
          abi: primeAbi,
          address: primeContractAddress,
          functionName: 'MINIMUM_STAKED_XVS',
        }) // TODO: get from API
      : undefined,
    accountAddress && primeContractAddress
      ? publicClient.readContract({
          abi: primeAbi,
          address: primeContractAddress,
          functionName: 'tokens',
          args: [accountAddress],
        })
      : undefined,
    // VAI related calls
    resilientOracleContractAddress && vai
      ? publicClient.readContract({
          address: resilientOracleContractAddress,
          abi: resilientOracleAbi,
          functionName: 'getPrice',
          args: [vai.address],
        })
      : undefined,
    vaiControllerContractAddress
      ? publicClient.readContract({
          address: vaiControllerContractAddress,
          abi: vaiControllerAbi,
          functionName: 'getVAIRepayRate',
        })
      : undefined,
  ]);

  const primeVTokenAddresses = unsafePrimeVTokenAddressesResult ?? [];
  const isUserPrime = userPrimeTokenResult?.[0] || false;

  let userCollateralVTokenAddresses: string[] | undefined;
  let userTokenBalances: TokenBalance[] | undefined;
  let userVTokenBalances: VTokenBalance[] | undefined;
  let userVaiBorrowBalanceMantissa: BigNumber | undefined;
  let userPrimeApyMap: Map<string, PrimeApy> | undefined;
  let userLegacyPoolEModeGroupId: number | undefined;

  if (accountAddress) {
    const [
      userCollaterals,
      userBalances,
      userVaiBorrowBalance,
      userPrimeApys,
      userLegacyPoolEModeGroupIdResult,
    ] = await Promise.all([
      getUserCollateralAddresses({
        chainId,
        accountAddress,
        legacyPoolComptrollerContractAddress,
        apiPools: apiPoolsResult.pools,
        publicClient,
      }),
      getUserTokenBalances({
        accountAddress,
        apiPools: apiPoolsResult.pools,
        chainId,
        tokens,
        publicClient,
        poolLensContractAddress,
        venusLensContractAddress,
      }),
      vaiControllerContractAddress
        ? getUserVaiBorrowBalance({
            accountAddress,
            publicClient,
            vaiControllerContractAddress,
          })
        : undefined,
      isUserPrime && primeContractAddress
        ? getUserPrimeApys({
            accountAddress,
            publicClient,
            primeContractAddress,
            primeVTokenAddresses,
          })
        : undefined,
      isEModeFeatureEnabled && legacyPoolComptrollerContractAddress
        ? publicClient.readContract({
            abi: legacyPoolComptrollerAbi,
            address: legacyPoolComptrollerContractAddress,
            functionName: 'userPoolId',
            args: [accountAddress],
          })
        : undefined,
    ]);

    userCollateralVTokenAddresses = userCollaterals.userCollateralAddresses;
    userVTokenBalances = userBalances.userVTokenBalances;
    userTokenBalances = userBalances.userTokenBalances;
    userVaiBorrowBalanceMantissa = userVaiBorrowBalance?.userVaiBorrowBalanceMantissa;
    userPrimeApyMap = userPrimeApys?.userPrimeApyMap;
    userLegacyPoolEModeGroupId = userLegacyPoolEModeGroupIdResult
      ? Number(userLegacyPoolEModeGroupIdResult)
      : undefined;
  }

  const userPoolEModeGroupIdMapping: Record<Address, number> = {};

  // E-mode groups are currently only enabled on the legacy Core Pool
  if (userLegacyPoolEModeGroupId && legacyPoolComptrollerContractAddress) {
    userPoolEModeGroupIdMapping[legacyPoolComptrollerContractAddress.toLowerCase() as Address] =
      userLegacyPoolEModeGroupId;
  }

  const pools = formatOutput({
    chainId,
    isUserConnected: !!accountAddress,
    tokens,
    currentBlockNumber: currentBlockNumberResult,
    apiPools: apiPoolsResult.pools,
    tokenMetadataMapping: apiPoolsResult.tokenMetadataMapping,
    userPrimeApyMap,
    userCollateralVTokenAddresses,
    userVTokenBalances,
    userTokenBalances,
    userVaiBorrowBalanceMantissa,
    userPoolEModeGroupIdMapping,
    vaiRepayRateMantissa: vaiRepayRateMantissaResult,
    vaiPriceMantissa: vaiPriceMantissaResult,
  });

  // Add Prime simulations
  // TODO: get Prime simulations from API
  const xvs = tokens.find(token => token.symbol === 'XVS');

  if (primeContractAddress && primeMinimumXvsToStakeMantissaResult && xvs) {
    await appendPrimeSimulationDistributions({
      assets: pools.reduce<Asset[]>((acc, pool) => acc.concat(pool.assets), []),
      primeContractAddress,
      primeVTokenAddresses,
      primeMinimumXvsToStakeMantissa: new BigNumber(
        primeMinimumXvsToStakeMantissaResult.toString(),
      ),
      xvs,
      chainId,
      publicClient,
    });
  }

  const output: GetPoolsQueryOutput = {
    pools,
    tokenMetadataMapping: apiPoolsResult.tokenMetadataMapping,
  };

  return output;
};
